/**
 * MSRP credential helpers.
 *
 * The dashboard authenticates to SCont with RFC 2617 digest, whose response is
 * derived from HA1 = MD5(username:realm:password). Computing HA1 needs the
 * plaintext password — but the digest itself only ever needs HA1. That gap is
 * what lets an SSO login work without a password: the gateway hands us a
 * ready-made HA1 and we feed it straight into the digest.
 *
 * The credential carried through Wrapper → Endpoint → MsrpAuth is therefore one
 * of two things, distinguished by prefix (same scheme as the gateway's
 * `src/utils/password.utils.ts`, keep the two in sync):
 *
 *   `h1_<md5hex>`  → a provisioned HA1, already bound to a realm
 *   `<plain>`      → the user's plaintext password (classic form login)
 *
 * A provisioned HA1 is realm-bound: it only validates against the realm it was
 * computed with (the gateway derives `<company_domain>.icont.ai`). If SCont ever
 * challenges with a different realm, digest fails — see the warning logged in
 * MsrpAuth.calculateHA1.
 */

export const HA1_PREFIX = "h1_";

/** A genuine HA1 is exactly 32 hex chars (MD5 hex digest). */
const HA1_HEX = /^[0-9a-f]{32}$/i;

/**
 * Extract a provisioned HA1 from a credential, or null when the credential is a
 * plaintext password.
 *
 * The 32-hex check guards the prefix-collision case: a real password that
 * happens to start with "h1_" (e.g. "h1_secret") is not an HA1 and must keep
 * working as plaintext.
 *
 * @param {string|null|undefined} credential
 * @returns {string|null} lowercase HA1 hex, or null
 */
export function extractProvisionedHa1(credential) {
  if (typeof credential !== "string" || !credential.startsWith(HA1_PREFIX)) return null;
  const rest = credential.slice(HA1_PREFIX.length);
  return HA1_HEX.test(rest) ? rest.toLowerCase() : null;
}

/**
 * True when the credential is a provisioned HA1 rather than a plaintext
 * password. Used to keep plaintext out of persisted storage.
 *
 * @param {string|null|undefined} credential
 */
export function isProvisionedHa1(credential) {
  return extractProvisionedHa1(credential) !== null;
}
